import { useCallback, useEffect, useMemo, useState } from 'react';
import { Plus, Eraser, Paintbrush, RefreshCw, Lock, CloudOff, Search } from 'lucide-react';
import { apiClients, apiAffaires } from '../api/client.js';
import Modal from '../components/Modal.jsx';
import EntityActions from '../components/EntityActions.jsx';
import ArchivedFilter from '../components/ArchivedFilter.jsx';
import ClientManagersSection from '../components/ClientManagersSection.jsx';

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  background: 'var(--glass)',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  fontSize: 14,
};

const primaryBtn = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  background: 'var(--accent)',
  color: '#fff',
  border: 'none',
  borderRadius: 'var(--radius-pill)',
  padding: '8px 16px',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
};

const secondaryBtn = {
  background: 'transparent',
  color: 'var(--text)',
  border: '1px solid var(--glass-border)',
  borderRadius: 'var(--radius-sm)',
  padding: '6px 14px',
  fontSize: 13,
  cursor: 'pointer',
};

const linkBtn = {
  background: 'transparent',
  color: 'var(--accent)',
  border: 'none',
  padding: 0,
  fontSize: 12,
  cursor: 'pointer',
  textDecoration: 'underline',
};

const DEFAULT_COLOR = '326496';

/** `<input type="color">` renvoie "#RRGGBB" — on stocke sans `#`. */
function colorWithHash(c) {
  if (!c) return `#${DEFAULT_COLOR}`;
  return c.startsWith('#') ? c : `#${c}`;
}
function colorStripHash(c) {
  if (!c) return null;
  return c.startsWith('#') ? c.slice(1) : c;
}

/** Minuscules sans accents : « État » doit matcher « etat ». */
function foldText(s) {
  return String(s ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
}

function formatDate(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('fr-FR');
}

/** Provenance qui rend le nom NON MODIFIABLE (miroir de `GESCOM_CLIENT_SOURCE`). */
const GESCOM_CLIENT_SOURCE = 'gescom_csv';

/**
 * Le nom est-il en LECTURE SEULE ici ?
 *
 * Le critère est la PROVENANCE (`clientSource`), exactement comme le refus
 * serveur (`assertNameEditable`) — et non la présence d'un `gescomId`. Une
 * fiche de source `crm` peut porter un code Gescom et rester renommable :
 * verrouiller sur le code affichait un champ grisé pour un renommage que le
 * serveur aurait accepté.
 *
 * Pour les fiches `gescom_csv`, on corrige dans Gescom : sinon le prochain
 * import écraserait la correction en laissant croire qu'elle avait été prise en
 * compte.
 */
function isFromGescom(client) {
  return client?.clientSource === GESCOM_CLIENT_SOURCE;
}

/** Pastille de provenance : Gescom, référentiel central, ou purement local. */
function SourcePill({ client }) {
  const gescom = isFromGescom(client);
  const linked = Boolean(client?.coreClientId);
  const code = client?.gescomId ? ` ${client.gescomId}` : '';
  const label = gescom
    ? `Gescom${code}`
    : linked
      ? `Référentiel central${code}`
      : 'Local';
  const title = gescom
    ? 'Client maîtrisé par Gescom — nom en lecture seule (corriger dans Gescom).'
    : linked
      ? 'Client rattaché au référentiel central synocore. Nom modifiable ici.'
      : 'Client non rattaché au référentiel central (cf. backfill:core-clients).';
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        padding: '2px 10px',
        borderRadius: 'var(--radius-pill)',
        fontSize: 11,
        background: 'var(--glass)',
        border: '1px solid var(--glass-border)',
        color: linked || gescom ? 'var(--text)' : 'var(--text-muted)',
        whiteSpace: 'nowrap',
      }}
    >
      {gescom ? <Lock size={11} /> : linked ? null : <CloudOff size={11} />}
      {label}
    </span>
  );
}

function ColorDot({ hex, size = 12 }) {
  return (
    <span
      title={hex ? `#${hex}` : 'Sans couleur'}
      style={{
        display: 'inline-block',
        width: size,
        height: size,
        borderRadius: '50%',
        background: hex ? `#${hex}` : 'transparent',
        border: '1px solid var(--glass-border)',
      }}
    />
  );
}

export default function ClientsView({ user }) {
  // #108 — hors ADMIN : lecture seule du périmètre (clients dont on est
  // responsable + clients de ses affaires). Le référentiel est maître dans
  // synocore ; toute écriture reste ADMIN côté serveur.
  const isAdmin = user?.role === 'ADMIN';
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archivedFilter, setArchivedFilter] = useState('active');
  const [q, setQ] = useState('');

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', color: null });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);

  // Sprint recolor — état affaires du client en cours d'édition
  const [affaires, setAffaires] = useState([]);
  const [affairesLoading, setAffairesLoading] = useState(false);
  const [includeArchivedAffaires, setIncludeArchivedAffaires] = useState(false);
  const [selectedAffaireIds, setSelectedAffaireIds] = useState(() => new Set());
  const [recoloring, setRecoloring] = useState(false);
  const [recolorError, setRecolorError] = useState(null);
  const [recolorInfo, setRecolorInfo] = useState(null);

  // Réplication : `{ clientId, status, reason }`. Renseigné quand une écriture a
  // bien été enregistrée dans le référentiel CENTRAL mais pas encore redescendue
  // localement. Ce n'est PAS une erreur — on ne revient jamais en arrière sur le
  // maître —, donc le bandeau est un avertissement, avec un bouton pour rejouer
  // la descente sans attendre le passage automatique de 15 minutes.
  const [mirrorWarning, setMirrorWarning] = useState(null);
  const [retrying, setRetrying] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncInfo, setSyncInfo] = useState(null);
  // Drapeau `CLIENT_MASTER_CORE` lu côté serveur. `null` = pas encore connu :
  // on n'affiche pas le bouton « Synchroniser » avant de savoir, plutôt que de
  // le montrer pour qu'il réponde une erreur (le référentiel n'est pas encore
  // maîtrisé par synocore sur tous les environnements).
  const [clientMasterCore, setClientMasterCore] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items: data } = await apiClients.list(archivedFilter);
      setItems(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [archivedFilter]);

  useEffect(() => {
    reload();
  }, [reload]);

  useEffect(() => {
    // `/clients/meta` est réservé à l'ADMIN : inutile ailleurs (aucun bouton).
    if (!isAdmin) return undefined;
    let cancelled = false;
    // Best-effort : une capacité inconnue laisse simplement le bouton masqué.
    apiClients
      .meta()
      .then((m) => {
        if (!cancelled) setClientMasterCore(Boolean(m?.clientMasterCore));
      })
      .catch(() => {
        if (!cancelled) setClientMasterCore(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAdmin]);

  const loadAffaires = useCallback(async (clientId, includeArchived) => {
    setAffairesLoading(true);
    try {
      const filter = includeArchived ? 'all' : 'active';
      const { items: rows } = await apiAffaires.list(filter, {
        conditions: [{ dimension: 'client', operator: 'is', values: [clientId] }],
      });
      setAffaires(rows);
      // Pré-sélectionne toutes les actives à l'ouverture.
      setSelectedAffaireIds(
        new Set(rows.filter((a) => !a.archivedAt).map((a) => a.id)),
      );
    } catch (e) {
      setRecolorError(e.message);
      setAffaires([]);
      setSelectedAffaireIds(new Set());
    } finally {
      setAffairesLoading(false);
    }
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ name: '', color: null });
    setFormError(null);
    setAffaires([]);
    setSelectedAffaireIds(new Set());
    setRecolorError(null);
    setRecolorInfo(null);
    setIncludeArchivedAffaires(false);
    setModalOpen(true);
  };

  const openEdit = (client) => {
    setEditing(client);
    setForm({ name: client.name, color: client.color ?? null });
    setFormError(null);
    setRecolorError(null);
    setRecolorInfo(null);
    setIncludeArchivedAffaires(false);
    setAffaires([]);
    setSelectedAffaireIds(new Set());
    setModalOpen(true);
    void loadAffaires(client.id, false);
  };

  // Recharge la liste des affaires quand on bascule "archivées".
  useEffect(() => {
    if (!modalOpen || !editing) return;
    void loadAffaires(editing.id, includeArchivedAffaires);
  }, [includeArchivedAffaires, modalOpen, editing, loadAffaires]);

  /** Retient l'avertissement de réplication porté par une réponse write-through. */
  const noteMirror = (res, fallbackClientId = null) => {
    const { client, mirror } = apiClients.unwrap(res);
    if (mirror && mirror.status && mirror.status !== 'OK') {
      setMirrorWarning({
        clientId: client?.id ?? fallbackClientId,
        status: mirror.status,
        reason: mirror.reason ?? null,
      });
    } else {
      setMirrorWarning(null);
    }
    return client;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setFormError(null);
    try {
      // Le nom d'un client Gescom n'est pas envoyé : il est en lecture seule, et
      // le champ est désactivé côté formulaire. Seule la couleur (locale) part.
      const nameLocked = editing && isFromGescom(editing);
      if (editing) {
        const payload = nameLocked ? { color: form.color } : { name: form.name, color: form.color };
        noteMirror(await apiClients.update(editing.id, payload), editing.id);
      } else {
        noteMirror(await apiClients.create({ name: form.name, color: form.color }));
      }
      setModalOpen(false);
      await reload();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const onArchive = async (client) => {
    if (!window.confirm(`Archiver le client "${client.name}" ?`)) return;
    noteMirror(await apiClients.archive(client.id), client.id);
    await reload();
  };

  const onRestore = async (client) => {
    noteMirror(await apiClients.restore(client.id), client.id);
    await reload();
  };

  /** Rejoue la descente ciblée du client concerné par l'avertissement. */
  const onRetryMirror = async () => {
    if (!mirrorWarning) return;
    setRetrying(true);
    try {
      if (mirrorWarning.clientId) {
        noteMirror(await apiClients.pull(mirrorWarning.clientId), mirrorWarning.clientId);
      } else {
        // Création dont le miroir n'a rien pu écrire : aucun id local à cibler,
        // la descente complète du périmètre est le seul recours. Elle part en
        // arrière-plan (202) — on le dit plutôt que de laisser croire que le
        // rechargement qui suit porte déjà le résultat.
        await apiClients.sync();
        setMirrorWarning(null);
        setSyncInfo(
          'Descente complète lancée en arrière-plan : recharger la liste dans quelques instants.',
        );
      }
      await reload();
    } catch (err) {
      setMirrorWarning((w) => ({ ...(w ?? {}), reason: err.message }));
    } finally {
      setRetrying(false);
    }
  };

  /**
   * Descente complète du périmètre rattaché (bouton de synchronisation).
   *
   * La route répond 202 : la descente tourne en fond. On le DIT, au lieu de
   * laisser croire que la liste rechargée juste après reflète déjà le résultat.
   */
  const onSync = async () => {
    setSyncing(true);
    setError(null);
    setSyncInfo(null);
    try {
      await apiClients.sync();
      setMirrorWarning(null);
      setSyncInfo(
        'Descente lancée. Elle tourne en arrière-plan : recharger la liste dans quelques instants (le détail est dans la console de batchs).',
      );
      await reload();
    } catch (err) {
      setError(err.message);
    } finally {
      setSyncing(false);
    }
  };

  // ----- Recolor affaires -----
  /** Nom en lecture seule : client descendu de Gescom via le référentiel. */
  const nameLocked = Boolean(editing && isFromGescom(editing));

  const selectedCount = selectedAffaireIds.size;
  // Filtre texte local : la liste complète est déjà chargée (quelques centaines
  // de clients au plus). Chaque mot doit apparaître dans le nom, le code Gescom ou un responsable.
  const visibleItems = useMemo(() => {
    const terms = foldText(q).split(/\s+/).filter(Boolean);
    if (terms.length === 0) return items;
    return items.filter((c) => {
      const managerNames = (c.managers ?? []).map((m) => `${m.firstName} ${m.lastName}`).join(' ');
      const haystack = foldText(`${c.name} ${c.gescomId ?? ''} ${managerNames}`);
      return terms.every((t) => haystack.includes(t));
    });
  }, [items, q]);
  // #108 — colonnes Responsables / Source / Actions : ADMIN uniquement.
  const colCount = isAdmin ? 6 : 3;

  const allSelected = useMemo(
    () => affaires.length > 0 && selectedCount === affaires.length,
    [affaires, selectedCount],
  );

  const toggleAffaire = (id) => {
    setSelectedAffaireIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedAffaireIds(new Set(affaires.map((a) => a.id)));
  const selectNone = () => setSelectedAffaireIds(new Set());
  const selectInvert = () =>
    setSelectedAffaireIds((prev) => {
      const next = new Set();
      for (const a of affaires) if (!prev.has(a.id)) next.add(a.id);
      return next;
    });

  const applyRecolor = async (clearMode) => {
    if (!editing) return;
    if (selectedCount === 0) return;
    const colorRaw = clearMode ? null : form.color;
    const label = clearMode ? 'effacer la couleur de' : `repeindre en #${colorRaw}`;
    if (!window.confirm(`Confirmer ${label} ${selectedCount} affaire(s) ?`)) return;
    setRecoloring(true);
    setRecolorError(null);
    setRecolorInfo(null);
    try {
      const { updated } = await apiClients.recolorAffaires(
        editing.id,
        colorRaw,
        Array.from(selectedAffaireIds),
      );
      setRecolorInfo(
        clearMode
          ? `${updated} affaire(s) sans couleur.`
          : `${updated} affaire(s) repeinte(s) en #${colorRaw}.`,
      );
      // Recharge la liste pour refléter les nouvelles couleurs dans les pastilles.
      await loadAffaires(editing.id, includeArchivedAffaires);
      await reload();
    } catch (e) {
      setRecolorError(e.message);
    } finally {
      setRecoloring(false);
    }
  };

  return (
    <div style={{ padding: '24px 32px', maxWidth: 1100, margin: '0 auto' }}>
      <div
        style={{
          position: 'sticky',
          top: 0,
          background: 'var(--bg)',
          paddingBottom: 12,
          zIndex: 5,
          display: 'flex',
          alignItems: 'center',
          gap: 16,
          marginBottom: 16,
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, color: 'var(--text)' }}>Clients</h1>
        <ArchivedFilter value={archivedFilter} onChange={setArchivedFilter} />
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-sm)',
            padding: '4px 10px',
            background: 'var(--glass)',
          }}
        >
          <Search size={14} style={{ color: 'var(--text-muted)' }} />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Rechercher (nom, code Gescom, responsable)…"
            style={{
              background: 'transparent',
              border: 'none',
              outline: 'none',
              color: 'var(--text)',
              fontSize: 13,
              width: 220,
            }}
          />
        </div>
        {clientMasterCore ? (
          <button
            type="button"
            onClick={onSync}
            disabled={syncing}
            title="Redescendre le référentiel client depuis synocore"
            style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: 6, marginLeft: 'auto', opacity: syncing ? 0.6 : 1 }}
          >
            <RefreshCw size={14} /> {syncing ? 'Synchronisation…' : 'Synchroniser'}
          </button>
        ) : null}
        {isAdmin ? (
          <button
            type="button"
            onClick={openCreate}
            style={{ ...primaryBtn, ...(clientMasterCore ? {} : { marginLeft: 'auto' }) }}
          >
            <Plus size={14} /> Nouveau client
          </button>
        ) : null}
      </div>

      {error ? (
        <div style={{ color: 'var(--danger)', marginBottom: 12 }}>{error}</div>
      ) : null}

      {syncInfo ? (
        <div style={{ color: 'var(--text-muted)', marginBottom: 12, fontSize: 13 }}>{syncInfo}</div>
      ) : null}

      {mirrorWarning ? (
        <div
          style={{
            marginBottom: 12,
            padding: '10px 14px',
            borderRadius: 'var(--radius-md)',
            background: 'var(--glass)',
            border: '1px solid var(--warning)',
            color: 'var(--text)',
            fontSize: 13,
            display: 'flex',
            alignItems: 'center',
            gap: 12,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ flex: 1, minWidth: 220 }}>
            {mirrorWarning.status === 'CONFLICT'
              ? 'Enregistré dans le référentiel central. Le nom n’a pas été répliqué localement : un autre client porte déjà ce nom. À corriger dans le référentiel central.'
              : 'Enregistré dans le référentiel central, réplication locale en attente.'}
            {mirrorWarning.reason ? (
              <span style={{ color: 'var(--text-muted)' }}> — {mirrorWarning.reason}</span>
            ) : null}
          </span>
          <button
            type="button"
            onClick={onRetryMirror}
            disabled={retrying}
            style={{ ...secondaryBtn, display: 'inline-flex', alignItems: 'center', gap: 6, opacity: retrying ? 0.6 : 1 }}
          >
            <RefreshCw size={13} /> {retrying ? 'Réessai…' : 'Réessayer'}
          </button>
        </div>
      ) : null}

      <div
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--glass-border)',
          borderRadius: 'var(--radius-md)',
          overflow: 'hidden',
          boxShadow: 'var(--shadow-card)',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: 'var(--glass)', textAlign: 'left' }}>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 110 }}>Couleurs</th>
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Nom</th>
              {isAdmin ? (
                <>
                  <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Responsables</th>
                  <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 190 }}>Source</th>
                </>
              ) : null}
              <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500 }}>Créé le</th>
              {isAdmin ? (
                <th style={{ padding: '10px 12px', color: 'var(--text-muted)', fontWeight: 500, width: 100 }}>Actions</th>
              ) : null}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 20, color: 'var(--text-muted)' }}>Chargement…</td>
              </tr>
            ) : visibleItems.length === 0 ? (
              <tr>
                <td colSpan={colCount} style={{ padding: 20, color: 'var(--text-muted)' }}>
                  {items.length === 0 ? 'Aucun client.' : 'Aucun client ne correspond à la recherche.'}
                </td>
              </tr>
            ) : (
              visibleItems.map((c) => {
                // Priorité : couleur explicite du client. Sinon, agrégation des affaires.
                const dots = c.color ? [c.color] : c.affaireColors ?? [];
                return (
                  <tr key={c.id} style={{ borderTop: '1px solid var(--glass-border)' }}>
                    <td style={{ padding: '10px 12px' }}>
                      {dots.length > 0 ? (
                        <span style={{ display: 'inline-flex', gap: 4, flexWrap: 'wrap' }}>
                          {dots.map((hex) => (
                            <ColorDot key={hex} hex={hex} />
                          ))}
                        </span>
                      ) : (
                        <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ padding: '10px 12px', color: 'var(--text)' }}>
                      {c.name}
                      {c.archivedAt ? (
                        <span style={{ marginLeft: 8, fontSize: 11, color: 'var(--text-muted)' }}>
                          (archivé)
                        </span>
                      ) : null}
                    </td>
                    {isAdmin ? (
                      <>
                        <td style={{ padding: '10px 12px', fontSize: 13, color: 'var(--text-muted)' }}>
                          {(c.managers ?? []).length > 0
                            ? c.managers.map((m) => `${m.firstName} ${m.lastName}`).join(', ')
                            : '—'}
                        </td>
                        <td style={{ padding: '10px 12px' }}>
                          <SourcePill client={c} />
                        </td>
                      </>
                    ) : null}
                    <td style={{ padding: '10px 12px', color: 'var(--text-muted)' }}>{formatDate(c.createdAt)}</td>
                    {isAdmin ? (
                      <td style={{ padding: '10px 12px' }}>
                        <EntityActions
                          archived={Boolean(c.archivedAt)}
                          onEdit={() => openEdit(c)}
                          onArchive={() => onArchive(c)}
                          onRestore={() => onRestore(c)}
                        />
                      </td>
                    ) : null}
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Modifier le client' : 'Nouveau client'}
        footer={
          <>
            <button type="button" onClick={() => setModalOpen(false)} style={secondaryBtn}>
              Annuler
            </button>
            <button
              type="submit"
              form="client-form"
              disabled={submitting || !form.name.trim()}
              style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}
            >
              Enregistrer
            </button>
          </>
        }
      >
        <form id="client-form" onSubmit={onSubmit}>
          <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
            Nom
          </label>
          <input
            type="text"
            value={form.name}
            onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
            required
            maxLength={255}
            autoFocus={!nameLocked}
            readOnly={nameLocked}
            disabled={nameLocked}
            title={nameLocked ? 'Nom maîtrisé par Gescom — non modifiable ici.' : undefined}
            style={{
              ...inputStyle,
              ...(nameLocked
                ? { opacity: 0.7, cursor: 'not-allowed', background: 'var(--card-bg)' }
                : {}),
            }}
          />
          {nameLocked ? (
            <div
              style={{
                marginTop: 6,
                fontSize: 12,
                color: 'var(--text-muted)',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
              }}
            >
              <Lock size={12} />
              Ce client vient de Gescom : son nom est en lecture seule. La correction se fait dans
              Gescom, puis redescend automatiquement — la modifier ici serait écrasée au prochain
              import.
            </div>
          ) : null}
          {editing ? (
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <SourcePill client={editing} />
              {editing.coreSyncedAt ? (
                <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  Répliqué le {formatDate(editing.coreSyncedAt)}
                </span>
              ) : null}
            </div>
          ) : null}

          <div style={{ marginTop: 14 }}>
            <label style={{ display: 'block', marginBottom: 6, fontSize: 13, color: 'var(--text-muted)' }}>
              Couleur par défaut
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <input
                type="color"
                value={colorWithHash(form.color)}
                onChange={(e) =>
                  setForm((f) => ({ ...f, color: colorStripHash(e.target.value) }))
                }
                style={{
                  width: 44,
                  height: 32,
                  padding: 0,
                  border: '1px solid var(--glass-border)',
                  borderRadius: 'var(--radius-sm)',
                  background: 'transparent',
                  cursor: 'pointer',
                }}
              />
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {form.color ? `#${form.color}` : 'aucune'}
              </span>
              {form.color ? (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, color: null }))}
                  style={linkBtn}
                >
                  effacer
                </button>
              ) : null}
            </div>
          </div>

          {formError ? (
            <div style={{ color: 'var(--danger)', marginTop: 8, fontSize: 13 }}>{formError}</div>
          ) : null}
        </form>

        {editing ? (
          <ClientManagersSection
            clientId={editing.id}
            onChange={(managers) =>
              setItems((rows) => rows.map((r) => (r.id === editing.id ? { ...r, managers } : r)))
            }
          />
        ) : null}

        {editing ? (
          <div
            style={{
              marginTop: 18,
              paddingTop: 14,
              borderTop: '1px solid var(--glass-border)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 14, color: 'var(--text)', fontWeight: 500 }}>
                Appliquer la couleur aux affaires
              </span>
              <label
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 12,
                  color: 'var(--text-muted)',
                  cursor: 'pointer',
                }}
              >
                <input
                  type="checkbox"
                  checked={includeArchivedAffaires}
                  onChange={(e) => setIncludeArchivedAffaires(e.target.checked)}
                />
                Inclure les archivées
              </label>
            </div>

            {affairesLoading ? (
              <div style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 13 }}>
                Chargement des affaires…
              </div>
            ) : affaires.length === 0 ? (
              <div style={{ marginTop: 10, color: 'var(--text-muted)', fontSize: 13 }}>
                Aucune affaire pour ce client.
              </div>
            ) : (
              <>
                <div
                  style={{
                    marginTop: 10,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    fontSize: 12,
                  }}
                >
                  <button type="button" onClick={selectAll} style={linkBtn}>
                    Tout cocher
                  </button>
                  <button type="button" onClick={selectNone} style={linkBtn}>
                    Tout décocher
                  </button>
                  <button type="button" onClick={selectInvert} style={linkBtn}>
                    Inverser
                  </button>
                  <span style={{ color: 'var(--text-muted)', marginLeft: 'auto' }}>
                    {selectedCount} / {affaires.length} sélectionnée(s)
                  </span>
                </div>

                <div
                  style={{
                    marginTop: 8,
                    maxHeight: 240,
                    overflowY: 'auto',
                    border: '1px solid var(--glass-border)',
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--glass)',
                  }}
                >
                  {affaires.map((a) => {
                    const checked = selectedAffaireIds.has(a.id);
                    return (
                      <label
                        key={a.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          padding: '6px 10px',
                          fontSize: 13,
                          color: 'var(--text)',
                          borderBottom: '1px solid var(--glass-border)',
                          cursor: 'pointer',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAffaire(a.id)}
                        />
                        <ColorDot hex={a.color} />
                        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {a.name}
                          {a.archivedAt ? (
                            <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-muted)' }}>
                              (archivée)
                            </span>
                          ) : null}
                        </span>
                        {a.code ? (
                          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.code}</span>
                        ) : null}
                      </label>
                    );
                  })}
                </div>

                <div style={{ marginTop: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={() => applyRecolor(false)}
                    disabled={recoloring || selectedCount === 0 || !form.color}
                    title={!form.color ? 'Choisis d\'abord une couleur ci-dessus' : ''}
                    style={{
                      ...primaryBtn,
                      opacity: recoloring || selectedCount === 0 || !form.color ? 0.5 : 1,
                    }}
                  >
                    <Paintbrush size={14} />
                    Appliquer #{form.color ?? '—'}
                  </button>
                  <button
                    type="button"
                    onClick={() => applyRecolor(true)}
                    disabled={recoloring || selectedCount === 0}
                    style={{
                      ...secondaryBtn,
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      opacity: recoloring || selectedCount === 0 ? 0.5 : 1,
                    }}
                  >
                    <Eraser size={14} />
                    Effacer la couleur
                  </button>
                </div>

                {recolorError ? (
                  <div style={{ color: 'var(--danger)', marginTop: 8, fontSize: 13 }}>
                    {recolorError}
                  </div>
                ) : null}
                {recolorInfo ? (
                  <div style={{ color: 'var(--success)', marginTop: 8, fontSize: 13 }}>
                    {recolorInfo}
                  </div>
                ) : null}
              </>
            )}
          </div>
        ) : null}
      </Modal>
    </div>
  );
}
